import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface SLOIndicator {
  key: string;
  label: string;
  target: string;
  unit: string;
  current: number | boolean | null;
  healthy: boolean;
}

interface StatusSummary {
  service: string;
  version: string;
  environment: string;
  status: 'ok' | 'degraded';
  uptime_seconds: number;
  started_at: string;
  now: string;
  database: { status: string; response_ms: number; error?: string };
  login_health: { success_rate_percent: number | null; sample_size: number };
  scheduler: { status: string; running: boolean };
  payroll: { last_run_at: string | null; payroll_month?: string | null };
  slo_indicators: SLOIndicator[];
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
}

function formatIndicator(item: SLOIndicator): string {
  if (item.current === null || item.current === undefined) return '—';
  if (item.unit === 'percent') return `${item.current}%`;
  if (item.unit === 'milliseconds') return `${item.current} ms`;
  if (item.unit === 'boolean') return item.current ? 'running' : 'stopped';
  return String(item.current);
}

export default function StatusPage(): JSX.Element {
  const [summary, setSummary] = useState<StatusSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const response = await api.get<StatusSummary>('/api/status/summary');
        if (cancelled) return;
        setSummary(response.data);
        setError(null);
        setLastChecked(new Date());
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unable to reach status endpoint');
        setLastChecked(new Date());
      }
    }

    load();
    const handle = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  const overall = summary?.status ?? (error ? 'degraded' : 'ok');
  const overallLabel = overall === 'ok' ? 'All systems operational' : 'Service degraded';

  return (
    <main className="shell status-shell" aria-labelledby="status-title">
      <header className="status-header">
        <h1 id="status-title">AfriHR — Service Status</h1>
        <p className="lede">
          Live snapshot of the AfriHR platform. This page refreshes every 30 seconds.
        </p>
      </header>

      <section
        className={`status-banner status-banner--${overall === 'ok' ? 'ok' : 'degraded'}`}
        role="status"
        aria-live="polite"
      >
        <strong>{overallLabel}</strong>
        {summary ? (
          <span>
            {' '}— version {summary.version}, environment {summary.environment}, uptime{' '}
            {formatUptime(summary.uptime_seconds)}
          </span>
        ) : null}
      </section>

      {error ? (
        <p role="alert" className="status-error">
          Unable to load status: {error}
        </p>
      ) : null}

      {summary ? (
        <>
          <h2>Service Level Indicators</h2>
          <table className="status-table">
            <thead>
              <tr>
                <th scope="col">Indicator</th>
                <th scope="col">Target</th>
                <th scope="col">Current</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.slo_indicators.map((item) => (
                <tr key={item.key}>
                  <td>{item.label}</td>
                  <td>{item.target}</td>
                  <td>{formatIndicator(item)}</td>
                  <td>
                    <span
                      className={`status-pill status-pill--${item.healthy ? 'ok' : 'degraded'}`}
                    >
                      {item.healthy ? 'Healthy' : 'Degraded'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Components</h2>
          <dl className="status-components">
            <div>
              <dt>Database</dt>
              <dd>
                {summary.database.status} · {summary.database.response_ms} ms
              </dd>
            </div>
            <div>
              <dt>Background scheduler</dt>
              <dd>{summary.scheduler.status}</dd>
            </div>
            <div>
              <dt>Last payroll run</dt>
              <dd>
                {summary.payroll.last_run_at
                  ? new Date(summary.payroll.last_run_at).toLocaleString()
                  : 'No runs yet'}
              </dd>
            </div>
            <div>
              <dt>Login success rate (5 min)</dt>
              <dd>
                {summary.login_health.success_rate_percent === null
                  ? `No recent attempts (${summary.login_health.sample_size} samples)`
                  : `${summary.login_health.success_rate_percent}% over ${summary.login_health.sample_size} attempts`}
              </dd>
            </div>
          </dl>
        </>
      ) : (
        !error && <p>Loading status…</p>
      )}

      <footer className="status-footer">
        <p>
          {lastChecked
            ? `Last checked ${lastChecked.toLocaleTimeString()}`
            : 'Checking…'}
        </p>
      </footer>
    </main>
  );
}
